import React from 'react';
import * as Sentry from '@sentry/nextjs';
import { GetStaticProps } from 'next';
import clsx from 'clsx';

import styles from './styles.module.scss';

import { withLayoutAndSession } from '@utils/hocs';
import { PageWithProvidersAndLayout } from '@utils/types/nextApp';
import { NYCLandingType } from '@utils/types/landings';
import { getNYCLanding } from '@api/landings/getNYCLanding';
import MetaHead from '@components/_shared/metaHead';
import HeroSection from '@components/nycLanding/heroSection';
import FourListings from '@components/nycLanding/fourListings';
import Outperformance from '@components/nycLanding/outperformance';
import HowItWorks from '@components/nycLanding/howItWorks';
import RoveAdvantage from '@components/nycLanding/roveAdvantage';
import Clientele from '@components/nycLanding/clientele';
import SuccessStories from '@components/nycLanding/successStories';
import Distribution from '@components/nycLanding/distribution';
import FAQ from '@components/nycLanding/faq';
import GetStarted from '@components/nycLanding/getStarted';

type Props = {
  landing: NYCLandingType;
};

const NYCLanding: PageWithProvidersAndLayout<Props> = (props: Props) => {
  const {
    landing: {
      meta,
      heroSection,
      fourListings,
      outperformance,
      roveAdvantage,
      clientele,
      successStories,
      howItWorks,
      distribution,
      faq,
      getStarted,
    },
  } = props;

  return (
    <>
      {meta && (
        <MetaHead
          title={meta.title}
          description={meta.description}
          ogImage={heroSection.backgroundImage}
        />
      )}

      <div className={clsx([styles.nycLanding, styles['dark']])}>
        <HeroSection {...heroSection} />

        <section className={styles.nycLanding__desktopOnly}>
          <div className={styles.nycLanding__separator} />

          <FourListings listings={fourListings} />

          <div className={styles.nycLanding__separator} />
        </section>

        <Outperformance {...outperformance} order={1} />

        <div className={styles.nycLanding__separator} />

        <RoveAdvantage {...roveAdvantage} order={2} />

        <div className={styles.nycLanding__separator} />

        <Clientele {...clientele} order={3} />

        <div className={styles.nycLanding__separator} />

        <SuccessStories {...successStories} order={4} />

        <div className={styles.nycLanding__separator} />

        <HowItWorks {...howItWorks} order={5} />

        <div className={styles.nycLanding__separator} />

        <Distribution {...distribution} order={6} />

        <div className={styles.nycLanding__separator} />

        <FAQ {...faq} order={7} />

        <div className={styles.nycLanding__separator} />

        <GetStarted {...getStarted} order={8} />

        <div className={styles.nycLanding__separator} />
      </div>
    </>
  );
};

export const getStaticProps: GetStaticProps<any> = async () => {
  const landing = await getNYCLanding();

  if (!landing) {
    Sentry.captureException(
      `NYCLanding: getStaticProps: CMS returned no data for landing`
    );

    return {
      notFound: true,
      revalidate: 10,
    };
  }

  return {
    props: {
      landing,
    },
    revalidate: 30,
  };
};

export default withLayoutAndSession(NYCLanding, {
  headerProps: { theme: 'dark' },
  footerProps: { theme: 'dark' },
});
